import { useSuspenseQuery } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { meQuery, qk } from "../api/admin";
import type { Me, ProviderName, Roles } from "../api/types";
import type { RouterContext } from "../routes/__root";

export function useMe(): Me {
  return useSuspenseQuery(meQuery).data;
}

/**
 * A member's roles: Sales rep, Support agent, Manager and Admin in any mix (ADR 0035). There is
 * no single role to fall back to: `Me` has no `role` field, so reading one fails typecheck.
 */
export function rolesOf(m: { roles: Roles }): Roles {
  return m.roles;
}

export function isAdmin(m: { roles: Roles }): boolean {
  return m.roles.admin;
}

/** Managers and admins: they see and coach reps. */
export function managesReps(m: { roles: Roles }): boolean {
  return m.roles.manager || m.roles.admin;
}

/** Whether the company owns this provider's account (ADR 0050, `Me.own_providers`). */
export function ownsProvider(m: Pick<Me, "own_providers">, kind: ProviderName): boolean {
  return (m.own_providers ?? []).includes(kind);
}

/**
 * Wholesale cost (Kite's carrier and provider costs) and the carrier tuning are on the Platform
 * console only (owner, 2026-11-01; DESIGN.md "Admin sidebar (console)"): no Admin surface shows
 * them, whatever the company owns. The server stops serving them once the ops host is set up.
 */
export function showsCosts(_m: Pick<Me, "own_providers">): boolean {
  return false;
}

/** Admins edit; managers get a read-only view. */
export function useCanEdit(): boolean {
  return isAdmin(useMe());
}

/** Route guard for admin-only screens. Managers land on Live now. */
export function requireAdmin({ context }: { context: RouterContext }): void {
  const me = context.queryClient.getQueryData<Me>(qk.me);
  if (me && !isAdmin(me)) throw redirect({ to: "/admin/live-now" });
}

/** Shown on the sign-in page when a member without Manager or Admin opens the console. */
export const REP_ONLY = "That page is for admins.";

/** Route guard for managers' screens (managers and admins). */
export function requireManager({ context }: { context: RouterContext }): void {
  const me = context.queryClient.getQueryData<Me>(qk.me);
  // The admin layout sends these members to App mode first; this is the backstop.
  if (me && !managesReps(me)) throw redirect({ to: "/signin", search: { error: "admin_required" } });
}
